import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { LogOut, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { useAuth } from '../../AuthContext';
import { buildNavSections } from './nav-config';
import { useManualWarehouses } from '../../hooks/useManualWarehouses';
import { NavItem, NavDropdown, NavSubDropdown } from './NavItem';
import { useOpenEscalationsCount } from './useEscalationBadge';
import type { NavItemConfig, NavPermission } from './types';
import './Sidebar.css';

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
}

/** Username allowlists for permission-gated nav items. Backend enforces the same rules. */
const PERMISSION_ALLOWLISTS: Record<NavPermission, readonly string[]> = {
  auditing: [], // unused — auditing section is visible to all staff today
  'va-payments': ['arthur', 'robin'],
};

function hasPermission(permission: NavPermission | undefined, username: string): boolean {
  if (!permission) return true;
  const allowed = PERMISSION_ALLOWLISTS[permission];
  if (!allowed || allowed.length === 0) return false;
  return allowed.includes(username.toLowerCase());
}

function sectionMatchesLocation(items: NavItemConfig[], pathname: string, search: string): boolean {
  return items.some(item => {
    if (item.children) return sectionMatchesLocation(item.children, pathname, search);
    if (!item.to) return false;
    const itemPath = item.to.split('?')[0];
    const pathMatch = item.end ? pathname === itemPath : pathname.startsWith(itemPath);
    if (!pathMatch) return false;
    if (item.activeSearch && !search.includes(item.activeSearch)) return false;
    return true;
  });
}

function Sidebar({ isOpen, onToggle }: SidebarProps) {
  const auth = useAuth();
  const logout = auth?.logout || (() => {});
  const user = auth?.user;
  const username = (user?.username || '').toLowerCase();
  const location = useLocation();
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});
  // Pending fleet approvals — portal is the sole approval surface, so the
  // sidebar carries the "you have decisions waiting" signal.
  const escalationCount = useOpenEscalationsCount();
  // Warehouses we run ourselves each get a nav entry. They come from the
  // registry rather than the static config, so opening one needs no edit here.
  const { warehouses: ownWarehouses } = useManualWarehouses();
  const navSections = useMemo(
    () => buildNavSections(ownWarehouses.map((w) => ({ code: w.code, name: w.name }))),
    [ownWarehouses],
  );
  const badgeFor = (item: NavItemConfig): number | undefined =>
    item.badgeSource === 'fleet-escalations' ? escalationCount : undefined;

  // Flyout: temporary hover-expansion while the sidebar is collapsed. The app
  // grid stays at the rail width; the sidebar overlays the content instead.
  const [flyout, setFlyout] = useState(false);
  const leaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // After clicking "collapse" the pointer is still inside the sidebar; suppress
  // the flyout until it leaves once, otherwise the sidebar would never shrink.
  const suppressFlyout = useRef(false);
  const expanded = isOpen || flyout;

  useEffect(() => () => { if (leaveTimer.current) clearTimeout(leaveTimer.current); }, []);

  const handleEnter = useCallback(() => {
    if (isOpen || suppressFlyout.current) return;
    if (leaveTimer.current) clearTimeout(leaveTimer.current);
    setFlyout(true);
  }, [isOpen]);

  // Sections containing the current route — auto-opened on navigation.
  const activeSections = useCallback(() => {
    const updates: Record<string, boolean> = {};
    navSections.forEach(section => {
      if (!section.label) return;
      const items = section.items.filter(item => hasPermission(item.permission, username));
      if (sectionMatchesLocation(items, location.pathname, location.search)) {
        updates[section.label] = true;
      }
    });
    return updates;
  }, [location.pathname, location.search, navSections, username]);

  const handleLeave = useCallback(() => {
    suppressFlyout.current = false;
    if (leaveTimer.current) clearTimeout(leaveTimer.current);
    leaveTimer.current = setTimeout(() => {
      if (!isOpen) setFlyout(false);
    }, 150);
  }, [isOpen]);

  const handleBlur = useCallback((e: React.FocusEvent<HTMLDivElement>) => {
    if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
    handleLeave();
  }, [handleLeave]);

  const handleToggle = useCallback(() => {
    if (isOpen) {
      // Collapsing: drop the flyout too, and keep it down until the pointer leaves.
      suppressFlyout.current = true;
      setFlyout(false);
    }
    onToggle();
  }, [isOpen, onToggle]);

  useEffect(() => {
    const updates = activeSections();
    if (Object.keys(updates).length > 0) {
      setOpenSections(prev => ({ ...prev, ...updates }));
    }
  }, [activeSections]);

  const toggleSection = useCallback((label: string) => {
    setOpenSections(prev => ({ ...prev, [label]: !prev[label] }));
  }, []);

  useEffect(() => {
    const content = document.querySelector('.sidebar-content');
    if (!content) return;
    const onWheel = (e: Event) => {
      const ev = e as WheelEvent;
      ev.stopPropagation();
      const canScrollUp = content.scrollTop > 0;
      const canScrollDown = content.scrollTop < content.scrollHeight - content.clientHeight;
      if ((ev.deltaY < 0 && !canScrollUp) || (ev.deltaY > 0 && !canScrollDown)) {
        ev.preventDefault();
      }
    };
    content.addEventListener('wheel', onWheel, { passive: false });
    return () => content.removeEventListener('wheel', onWheel);
  }, []);

  const displayName = (user?.name as string) || user?.username || '';
  const initial = displayName[0]?.toUpperCase() || '?';
  const isAdmin = Boolean(user?.['is_admin']);

  return (
    <div
      className={`sidebar ${expanded ? 'sidebar-open' : 'sidebar-collapsed'}${!isOpen && flyout ? ' sidebar-flyout' : ''}`}
      aria-expanded={expanded}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      onFocusCapture={handleEnter}
      onBlurCapture={handleBlur}
    >
      <div className="sidebar-header">
        <div className="sidebar-brand">
          <span className="sidebar-brand-icon">F</span>
          <span className="sidebar-brand-name">Fox &amp; Fable</span>
        </div>
      </div>

      <div className="sidebar-content">
        <nav className="sidebar-nav">
          <ul>
            {navSections.map((section, i) => {
              if (!hasPermission(section.permission, username)) return null;
              const items = section.items.filter(item => hasPermission(item.permission, username));
              if (items.length === 0) return null;

              if (!section.label) {
                return (
                  <React.Fragment key={i}>
                    {items.map(item => (
                      <NavItem key={item.to} to={item.to!} icon={item.icon} end={item.end} activeSearch={item.activeSearch} badge={badgeFor(item)}>
                        {item.label}
                      </NavItem>
                    ))}
                  </React.Fragment>
                );
              }

              const isActive = sectionMatchesLocation(items, location.pathname, location.search);
              const sectionOpen = !!openSections[section.label];
              const sectionBadge = items.reduce((sum, item) => sum + (badgeFor(item) ?? 0), 0);

              return (
                <NavDropdown
                  key={section.label}
                  label={section.label}
                  icon={section.icon}
                  isActive={isActive}
                  isOpen={sectionOpen && expanded}
                  onToggle={() => toggleSection(section.label!)}
                  badge={sectionBadge}
                >
                  {items.map(item => {
                    if (item.children) {
                      const childActive = sectionMatchesLocation(item.children, location.pathname, location.search);
                      return (
                        <NavSubDropdown key={item.label} label={item.label} icon={item.icon} isActive={childActive}>
                          {item.children.map(child => (
                            <NavItem key={child.to} to={child.to!} icon={child.icon} end={child.end} activeSearch={child.activeSearch} sub>
                              {child.label}
                            </NavItem>
                          ))}
                        </NavSubDropdown>
                      );
                    }
                    return (
                      <NavItem key={item.to} to={item.to!} icon={item.icon} end={item.end} activeSearch={item.activeSearch} sub badge={badgeFor(item)}>
                        {item.label}
                      </NavItem>
                    );
                  })}
                </NavDropdown>
              );
            })}
          </ul>
        </nav>
      </div>

      <div className="sidebar-footer">
        <div className="sidebar-user">
          <div className="sidebar-user-avatar">{initial}</div>
          <div className="sidebar-user-info">
            <span className="sidebar-user-name">{displayName}</span>
            {isAdmin && <span className="sidebar-user-role">Admin</span>}
          </div>
        </div>
        <div className="sidebar-footer-actions">
          <button onClick={logout} className="logout-button" title="Logout">
            <LogOut size={15} />
            <span>Logout</span>
          </button>
          <button
            onClick={handleToggle}
            className="sidebar-toggle-button"
            title={isOpen ? 'Collapse sidebar' : 'Pin sidebar open'}
            aria-label={isOpen ? 'Collapse sidebar' : 'Pin sidebar open'}
          >
            {isOpen ? <PanelLeftClose size={15} /> : <PanelLeftOpen size={15} />}
          </button>
        </div>
      </div>
    </div>
  );
}

export default Sidebar;
